import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  Tag,
  TagLabel,
  Icon,
  Box,
  useToast,
} from '@chakra-ui/react';
import { FiZap, FiArrowRightCircle } from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { addTagToDealAndContact } from '../../../utils/crmTags';
import {
  getActiveEnrollmentCountByTemplate,
  unenrollContactFromCampaignByTemplate,
} from '../../../api/supabase/automations';

/**
 * MoveToNurtureModal
 *
 * Two-step confirmation shown after a user logs an activity with Next Step =
 * "Nurture" on a deal whose source maps to a nurture route.
 *   Step 1 (confirm): add the nurture trigger tag to the contact + deal.
 *   Step 2 (remove):  if the lead is still enrolled in the main campaign,
 *                     confirm removing them from it.
 * Writes a single consolidated activity note and calls onComplete so the Deal
 * page can update local tags/timeline immediately.
 */
export default function MoveToNurtureModal({ isOpen, onClose, deal, contact, route, onComplete }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const removeBtnRef = useRef(null);

  const [step, setStep] = useState('confirm'); // 'confirm' | 'remove'
  const [isWorking, setIsWorking] = useState(false);
  const [mainEnrollmentCount, setMainEnrollmentCount] = useState(0);
  const [newDealTags, setNewDealTags] = useState(null);

  // Reset internal state whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setIsWorking(false);
      setMainEnrollmentCount(0);
      setNewDealTags(null);
    }
  }, [isOpen]);

  if (!deal || !contact || !route) return null;

  const contactName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'this lead';
  const ownerId = deal.ownerId || currentUser?.uid || null;

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  // Insert the consolidated timeline note and notify the Deal page.
  const finalize = async ({ removedFromMain, dealTags }) => {
    let transformedActivity = null;

    const changes = [`Added "${route.nurtureTag}" tag — enrolling in ${route.label}`];
    if (removedFromMain) {
      changes.push(`Removed from ${route.mainCampaignName}`);
    } else if (mainEnrollmentCount > 0) {
      changes.push(`Kept in ${route.mainCampaignName}`);
    }

    const content = removedFromMain
      ? `Moved to ${route.label}: added "${route.nurtureTag}" tag and removed from ${route.mainCampaignName}.`
      : mainEnrollmentCount > 0
        ? `Added to ${route.label} ("${route.nurtureTag}" tag). Kept in ${route.mainCampaignName}.`
        : `Moved to ${route.label}: added "${route.nurtureTag}" tag.`;

    if (isSupabaseConfigured() && currentUser?.uid) {
      try {
        const { data: newActivity } = await supabase
          .from('crm_activities')
          .insert({
            deal_id: deal.id,
            author_id: currentUser.uid,
            type: 'update',
            content,
            metadata: {
              authorName: currentUser.displayName || currentUser.email,
              authorPhotoURL: currentUser.photoURL || '',
              changes,
              nurtureRoute: route.source,
              nurtureTag: route.nurtureTag,
              removedFromMain: !!removedFromMain,
            },
          })
          .select()
          .single();

        if (newActivity) {
          transformedActivity = {
            id: newActivity.id,
            text: newActivity.content,
            authorId: newActivity.author_id,
            timestamp: newActivity.created_at,
            activityType: newActivity.type,
            ...(newActivity.metadata || {}),
          };
        }
      } catch (err) {
        // Non-critical: the tag/unenroll already succeeded.
        console.warn('[MoveToNurtureModal] activity note failed (non-critical):', err);
      }
    }

    if (onComplete) {
      onComplete({ newDealTags: dealTags, activity: transformedActivity });
    }
  };

  // Step 1 -> add tag, then branch on main-campaign enrollment.
  const handleMoveToNurture = async () => {
    setIsWorking(true);
    try {
      const { dealTags } = await addTagToDealAndContact(
        deal.id,
        contact.id,
        route.nurtureTag,
        ownerId,
        { label: route.label, color: 'purple' }
      );
      setNewDealTags(dealTags);

      let count = 0;
      try {
        count = await getActiveEnrollmentCountByTemplate(contact.id, route.mainCampaignTemplateId);
      } catch (err) {
        console.warn('[MoveToNurtureModal] enrollment lookup failed:', err);
      }
      setMainEnrollmentCount(count);

      if (count > 0) {
        setIsWorking(false);
        setStep('remove');
        return;
      }

      // Not in the main campaign — finish in one step.
      await finalize({ removedFromMain: false, dealTags });
      toast({
        title: `Added to ${route.label}`,
        description: `Tag "${route.nurtureTag}" applied. ${contactName} was not in ${route.mainCampaignName}.`,
        status: 'success',
        duration: 4000,
      });
      onClose();
    } catch (err) {
      console.error('[MoveToNurtureModal] move to nurture failed:', err);
      toast({
        title: 'Could not move to nurture',
        description: err.message || 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      setIsWorking(false);
    }
  };

  // Step 2 -> resolve the "remove from main campaign?" choice.
  const handleResolveRemoval = async (removedFromMain) => {
    setIsWorking(true);
    try {
      if (removedFromMain) {
        await unenrollContactFromCampaignByTemplate(contact.id, route.mainCampaignTemplateId);
      }
      await finalize({ removedFromMain, dealTags: newDealTags });
      toast({
        title: removedFromMain ? 'Moved to nurture' : 'Added to nurture',
        description: removedFromMain
          ? `${contactName} was added to ${route.label} and removed from ${route.mainCampaignName}.`
          : `${contactName} was added to ${route.label} and kept in ${route.mainCampaignName}.`,
        status: 'success',
        duration: 4000,
      });
      onClose();
    } catch (err) {
      console.error('[MoveToNurtureModal] removal step failed:', err);
      toast({
        title: 'Could not update the main campaign',
        description: err.message || 'Unknown error',
        status: 'error',
        duration: 5000,
      });
      setIsWorking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      closeOnOverlayClick={!isWorking}
      initialFocusRef={step === 'remove' ? removeBtnRef : undefined}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiZap} color="purple.500" />
            <Text>{step === 'confirm' ? 'Move to Nurture?' : 'Turn off the current campaign?'}</Text>
          </HStack>
        </ModalHeader>
        {!isWorking && <ModalCloseButton />}

        <ModalBody>
          {step === 'confirm' ? (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                <strong>{contactName}</strong> came from{' '}
                <Tag size="sm" colorScheme="blue" borderRadius="full"><TagLabel>{route.source}</TagLabel></Tag>.
                Move them into the <strong>{route.label}</strong> campaign?
              </Text>
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  This adds the{' '}
                  <Tag size="sm" colorScheme="purple" borderRadius="full"><TagLabel>{route.nurtureTag}</TagLabel></Tag>{' '}
                  tag, which triggers the {route.label} sequence.
                </Box>
              </Alert>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                <strong>{contactName}</strong> has{' '}
                <strong>{mainEnrollmentCount}</strong> pending step{mainEnrollmentCount === 1 ? '' : 's'} in{' '}
                <strong>{route.mainCampaignName}</strong>.
              </Text>
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  Remove this lead from <strong>{route.mainCampaignName}</strong> so they stop receiving
                  those messages while in nurture? This only affects this lead, not the campaign for everyone.
                </Box>
              </Alert>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {step === 'confirm' ? (
            <>
              <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isWorking}>
                Not now
              </Button>
              <Button
                colorScheme="purple"
                leftIcon={<FiArrowRightCircle />}
                onClick={handleMoveToNurture}
                isLoading={isWorking}
                loadingText="Moving..."
              >
                Yes, move to Nurture
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" mr={3} onClick={() => handleResolveRemoval(false)} isDisabled={isWorking}>
                Keep both
              </Button>
              <Button
                ref={removeBtnRef}
                colorScheme="blue"
                onClick={() => handleResolveRemoval(true)}
                isLoading={isWorking}
                loadingText="Removing..."
              >
                Yes, remove from {route.mainCampaignName}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
